import React, { useEffect, useRef, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { ReactComponent as SearchMagnifyIcon } from '../../assets/svg/SearchMagnifyIcon.svg';
import VerticalOptionsButton from './VerticalOptionsButton';
import ConfirmationModal from '../modals/ConfirmationModal';
import CreateEditUserModal from './CreateEditUserModal';
import ChangeSubscriptionModal from './ChangeSubscriptionModal';
import ChangePasswordModal from './ChangePasswordModal';
import FilterUserComponent from './FilterUserComponent';
import { deleteUser, getSubscriptionCatalog, getUsers } from '../../server/server';
import InfiniteScroll from 'react-infinite-scroll-component';
import Loading from '../Loading';
import { buddyFaces } from '../../utils/BuddyConstants';
import { useNavigate } from 'react-router';

const limit = 20;
const bearNames = ['Girl', 'Guy', 'Bear', 'Meerkat', 'Dragon', 'Robot'];
type SortField = 'lastLogin' | 'createdAt' | null;
type SortOrder = 'asc' | 'desc';

function UserManagement() {
	const navigate = useNavigate();
	const [users, setUsers] = useState<any[]>([]);
	const totalUsers = useRef(0);
	const [subscriptions, setSubscriptions] = useState<any[]>([]);
	const [searchText, setSearchText] = useState('');
	const [loading, setLoading] = useState(true);
	const timeout = useRef<any>(null);
	const [userProfileModalOpen, setUserProfileModalOpen] = useState(false);
	interface IUser {
		id: string;
		[key: string]: any;
	}
	const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
	const [deleteUserModal, setDeleteUserModal] = useState(false);
	const [createEditUserModal, setCreateEditUserModal] = useState(false);
	const [changeSubscriptionModal, setChangeSubscriptionModal] = useState(false);
	const [changePasswordModal, setChangePasswordModal] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [resultsNum, setResultsNum] = useState(0);
	const [hasMore, setHasMore] = useState(true);
	const isFirstMount = useRef(true);
	const [type, setType] = useState('');
	const [subscription, setSubscription] = useState('');
	const [sortField, setSortField] = useState<SortField>(null);
	const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

	useEffect(() => {
		loadSubscriptions();
	}, []);

	const loadSubscriptions = async () => {
		const resp = await getSubscriptionCatalog();
		if (resp.status === 200) {
			const subList = resp.data.map((item: any) => {
				return item.codeName;
			});
			setSubscriptions(['all', ...subList]);
		}
	};

	useEffect(() => {
		if (isFirstMount.current) {
			isFirstMount.current = false;
			return;
		}
		reloadList();
	}, [type, subscription]);

	useEffect(() => {
		loadUsers();
	}, [resultsNum]);

	const loadUsers = async (searchTerm: string = searchText) => {
		const isInitialLoad = resultsNum === 0;

		try {
			isInitialLoad ? setLoading(true) : setLoadingMore(true);

			const resp = await getUsers(limit, resultsNum, searchTerm);

			if (resp.status !== 200) return;

			const { items = [], total = 0 } = resp.data ?? {};
			totalUsers.current = total;

			setHasMore(total > (isInitialLoad ? items.length : users.length + items.length));

			setUsers((prev) => (isInitialLoad ? items : [...prev, ...items]));
		} catch (error) {
			console.error('Failed to load users:', error);
		} finally {
			setLoading(false);
			setLoadingMore(false);
		}
	};

	const setTypeFilter = (typeFilter: string) => {
		if (typeFilter === 'all') setType('');
		else {
			setType(typeFilter);
		}
	};

	const setSubFilter = (subFilter: string) => {
		if (subFilter === 'all') setSubscription('');
		else {
			setSubscription(subFilter);
		}
	};

	const openUserProfileScreen = (user: any) => {
		navigate(`/user/${user?.id}`);
		setSelectedUser(user);
	};

	const closeUserProfileScreen = () => {
		setUserProfileModalOpen(false);
	};

	const openDeleteUserModal = (user: any) => {
		setSelectedUser(user);
		setDeleteUserModal(true);
	};

	const closeDeleteUserModal = () => {
		setDeleteUserModal(false);
	};

	const openCreateUserModal = (user: any) => {
		setCreateEditUserModal(true);
		setSelectedUser(user);
	};

	const closeCreateUserModal = () => {
		setCreateEditUserModal(false);
	};

	const openChangeSubscription = (user: any) => {
		setChangeSubscriptionModal(true);
		setSelectedUser(user);
	};

	const closeChangeSubscription = () => {
		setChangeSubscriptionModal(false);
	};

	const openChangePassword = (user: any) => {
		setSelectedUser(user);
		setChangePasswordModal(true);
	};

	const closeChangePassword = () => {
		setChangePasswordModal(false);
	};

	const removeUser = async () => {
		setUserProfileModalOpen(false);
		if (selectedUser) {
			const resp = await deleteUser(selectedUser?.id);
			if (resp.status === 200) {
				setUsers((prev) => prev.filter((user) => user.id !== selectedUser.id));
				totalUsers.current -= 1;
			}
		}
	};

	const handleChange = (e: any) => {
		e.preventDefault();
		setSearchText(e.target.value);
		if (timeout !== null) {
			clearTimeout(timeout.current);
		}
		timeout.current = setTimeout(() => {
			if (resultsNum === 0) loadUsers(e.target.value);
			else setResultsNum(0);
		}, 700);
	};

	const reloadList = () => {
		if (resultsNum === 0) loadUsers();
		else setResultsNum(0);
	};

	const handleSort = (field: SortField) => {
		if (sortField !== field) {
			setSortField(field);
			setSortOrder('asc');
			return;
		}

		if (sortOrder === 'asc') {
			setSortOrder('desc');
			return;
		}

		setSortField(null);
		setSortOrder('asc');
	};

	const sortedUsers = [...users].sort((a, b) => {
		if (!sortField) return 0;

		const dateA = new Date(a[sortField]).getTime();
		const dateB = new Date(b[sortField]).getTime();

		return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
	});

	return (
		<div id='scroll-inf-task' className='flex h-screen flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[40px]'>
			<div className='w-full max-w-[1200px] px-[10px]'>
				<div className='flex min-h-[calc(100vh-80px)] w-full flex-col rounded-[10px] bg-white px-[40px] py-[30px]'>
					<div className='mb-[30px]'>
						<p className='text-[40px] font-bold leading-[50px] text-darkViolet'>User management</p>
						<p className='mt-[8px] text-[14px] font-medium leading-5 text-mudPurple'>Manage all users</p>
					</div>

					<div className='mb-[30px] flex flex-row items-center gap-4'>
						<div className='flex flex-1 flex-row items-center gap-2'>
							<p className='text-[20px] font-semibold leading-6 text-primaryViolet'>All users:</p>
							<p className='text-[20px] font-semibold leading-6 text-mudPurple'>{totalUsers.current}</p>
						</div>
						<div className='flex h-[50px] flex-row items-center rounded-full border border-primaryViolet px-4 py-1'>
							<input type='text' placeholder='Search' value={searchText} onChange={(e: any) => handleChange(e)} className='h-full flex-1 text-[18px] font-semibold leading-6 text-primaryViolet outline-none placeholder:font-medium placeholder:text-mudPurple' autoComplete='off' />
							<SearchMagnifyIcon />
						</div>
						<div className='flex h-[50px] min-w-[180px] cursor-pointer items-center justify-center rounded-full border border-primaryViolet bg-white px-3 text-[18px] font-semibold leading-6 text-primaryViolet transition hover:bg-primaryViolet hover:text-brandYellow' onClick={() => openCreateUserModal(null)}>
							Add new user
						</div>
					</div>

					{users.length === 0 && !loading && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<p className='text-center text-[14px] font-medium leading-5 text-mudPurple'>
								This list is empty right now.
								<br /> Consider adding new users...
							</p>
						</div>
					)}

					{!loading && users.length !== 0 && (
						<InfiniteScroll
							dataLength={users.length}
							next={() => {
								setResultsNum(users.length);
							}}
							hasMore={hasMore}
							scrollableTarget='scroll-inf-task'
							inverse={false}
							loader={undefined}
						>
							<div className='w-full overflow-hidden rounded-[10px] border border-[#EAECF0]'>
								<div className='grid w-full grid-cols-[50%_20%_20%_10%] bg-[#F9FAFB] px-6 py-3'>
									<div className='text-[14px] font-semibold leading-5 text-[#475467]'>User name</div>

									<div className='flex cursor-pointer items-center gap-1 text-[14px] font-semibold leading-5 text-[#475467]' onClick={() => handleSort('lastLogin')}>
										Last active
										{sortField === 'lastLogin' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
									</div>

									<div className='flex cursor-pointer items-center gap-1 text-[14px] font-semibold leading-5 text-[#475467]' onClick={() => handleSort('createdAt')}>
										Date added
										{sortField === 'createdAt' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
									</div>

									<div className='text-center text-[14px] font-semibold leading-5 text-[#475467]'>Action</div>
								</div>
								{sortedUsers.map((user: any, index: number) => (
									<UserRow key={user.id} user={user} openUserProfileModal={openUserProfileScreen} openCreateUserModal={openCreateUserModal} openDeleteUserModal={openDeleteUserModal} openChangePasswordModal={openChangePassword} isLast={index === users.length - 1} />
								))}
							</div>
						</InfiniteScroll>
					)}

					{loading && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<Loading size={70} primaryColor='#5E639B' stroke={3} />
						</div>
					)}
				</div>
			</div>
			<ConfirmationModal modalIsOpen={deleteUserModal} closeModal={closeDeleteUserModal} confirmation={removeUser} message={'Do you want to delete this user?'} subMessage='The user will be permanently deleted.' />
			<CreateEditUserModal modalIsOpen={createEditUserModal} closeModal={closeCreateUserModal} user={selectedUser} onSuccess={reloadList} />
			<ChangeSubscriptionModal modalIsOpen={changeSubscriptionModal} closeModal={closeChangeSubscription} user={selectedUser} reloadList={reloadList} />
			<ChangePasswordModal modalIsOpen={changePasswordModal} closeModal={closeChangePassword} user={selectedUser} />
		</div>
	);
}

const UserRow = ({ user, openUserProfileModal, openCreateUserModal, openDeleteUserModal, openChangePasswordModal, isLast }: { user: any; openUserProfileModal: any; openCreateUserModal: any; openDeleteUserModal: any; openChangePasswordModal: any; isLast: boolean }) => {
	return (
		<div className={`grid w-full cursor-pointer grid-cols-[50%_20%_20%_10%] px-6 py-4 transition-colors hover:bg-[#F9FAFB] border-[#EAECF0] ${!isLast ? 'border-b ' : ''}`} onClick={() => openUserProfileModal(user)}>
			<div className='flex flex-row items-center gap-3'>
				<div className='flex h-[40px] min-w-[40px] items-center justify-center overflow-hidden rounded-full border border-[#D0D5DD] bg-[#F2F4F7]'>
					<FaUser className='h-[20px] w-[20px] text-[#98A2B3]' />
				</div>
				<div className='flex flex-col'>
					<p className='text-[14px] font-medium leading-5 text-[#101828]'>
						{user.firstName} {user.lastName}
					</p>
					<p className='text-[14px] font-normal leading-5 text-[#475467]'>{user.email}</p>
				</div>
			</div>
			<div className='flex items-center'>
				<p className='text-[14px] font-normal leading-5 text-[#475467]'>
					{new Date(user.lastLogin).toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'short',
						day: 'numeric',
					})}
				</p>
			</div>
			<div className='flex items-center'>
				<p className='text-[14px] font-normal leading-5 text-[#475467]'>
					{new Date(user.createdAt).toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'short',
						day: 'numeric',
					})}
				</p>
			</div>

			<div className='flex items-center justify-center' onClick={(e) => e.stopPropagation()}>
				<VerticalOptionsButton openUserProfileModal={openUserProfileModal} editProfile={openCreateUserModal} changePassword={openChangePasswordModal} deleteProfile={openDeleteUserModal} user={user} />
			</div>
		</div>
	);
};

export default UserManagement;
